/**
 * Audit History Dialog Component
 * 
 * Main dialog component for displaying timesheet audit history
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuditHistory } from '@/hooks/useAuditHistory';
import { AuditSummary } from './AuditSummary';
import { AuditTimeline } from './AuditTimeline';
import { format } from 'date-fns';

interface AuditHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timesheetId: string;
  employeeName: string;
  weekEnding: Date;
}

export function AuditHistoryDialog({
  open,
  onOpenChange,
  timesheetId,
  employeeName,
  weekEnding,
}: AuditHistoryDialogProps) {
  const { auditEntries, auditSummary, hasHistory, isLoading, error } = useAuditHistory(timesheetId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Timesheet History - Week Ending {format(weekEnding, 'MMM d, yyyy')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Employee: {employeeName}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-1">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="text-6xl mb-4 animate-pulse">...</div>
              <h3 className="text-lg font-semibold mb-2">Loading History</h3>
              <p className="text-sm">
                Fetching approval history...
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="text-6xl mb-4">!</div>
              <h3 className="text-lg font-semibold mb-2 text-red-600">Error Loading History</h3>
              <p className="text-sm text-red-500">
                {error}
              </p>
            </div>
          ) : hasHistory ? (
            <div className="space-y-4">
              <AuditSummary summary={auditSummary} />
              <AuditTimeline entries={auditEntries} />
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <div className="text-6xl mb-4">...</div>
              <h3 className="text-lg font-semibold mb-2">No History Available</h3>
              <p className="text-sm">
                This timesheet doesn't have any audit history yet.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
